import { useQuery } from '@tanstack/react-query';

import { useOrg } from '@/contexts/OrgContext';
import { Select } from '@/lib/base/forms/select/select';
import { createSchemaClient } from '@/lib/crm/core/SchemaClient';
import { entityTypesQueryKey } from '@/lib/crm/hooks/useEntityTypeSchema';
import { getPropertyTypeIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';

import { SelectInputError, SelectInputLoading } from './SelectInputStates';
import type { PropertyInputProps } from './TextInput';

export const EntityTypeSelectInput: React.FC<PropertyInputProps> = ({
  propertyType,
  value,
  onChange,
  disabled = false,
  inputRef,
  placeholder,
  size = 'sm',
}) => {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;

  const { data, isLoading, error } = useQuery({
    queryKey: entityTypesQueryKey(orgId),
    queryFn: () => {
      if (!orgId) {
        throw new Error('No organization selected');
      }
      return createSchemaClient(orgId).getObjectTypes();
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  // When the property restricts selectable entity types by kind
  // (field_type_config.entity_kinds — e.g. validation rules → case/party/asset),
  // only show object types whose entity_kind intersects the allowed set.
  const allowedKinds = propertyType.field_type_config?.entity_kinds;
  const items = (data ?? [])
    .filter(
      s =>
        !allowedKinds?.length ||
        s.entity_kind.some(kind => allowedKinds.includes(kind))
    )
    .map(s => ({
      id: s.entity_type_name,
      label: s.label,
    }));

  const Icon = getPropertyTypeIconComponent(propertyType);

  if (isLoading) {
    return <SelectInputLoading />;
  }

  if (error) {
    return <SelectInputError error={error} />;
  }

  return (
    <Select.ComboBox
      placeholder={placeholder || `Select ${propertyType.label.toLowerCase()}`}
      size={size}
      items={items}
      selectedKey={value !== null && value !== undefined ? String(value) : null}
      onSelectionChange={key =>
        onChange(key !== null && key !== undefined ? String(key) : null)
      }
      isDisabled={disabled}
      shortcut={false}
      aria-label={propertyType.label}
      icon={Icon}
      ref={inputRef}
    >
      {item => <Select.Item id={item.id}>{item.label}</Select.Item>}
    </Select.ComboBox>
  );
};
